import { useEffect, useMemo, useState } from 'react';

import { useUserExperiments } from '~hooks/queries/users/query';
import { useUserId } from '~hooks/useUserId';

import { EXPERIMENT_VARIANTS } from '../constants';
import { UseExperimentResult } from '../types';

const AB_TEST_QA_KEY = 'ab_test_qa';

const useLocalStorageOverride = (): boolean | null => {
  const [override, setOverride] = useState<boolean | null>(null);

  useEffect(() => {
    const value = localStorage.getItem(AB_TEST_QA_KEY);
    if (value === 'true') setOverride(true);
    else if (value === 'false') setOverride(false);
  }, []);

  return override;
};

export function useExperiment(experimentKey: string): UseExperimentResult {
  const userId = useUserId();
  const { data: userExperiments, isLoading } = useUserExperiments({ userId });
  const localStorageOverride = useLocalStorageOverride();

  const variant = useMemo(() => {
    return userExperiments?.Data?.data?.find((exp) => exp.experimentKey === experimentKey)?.variant;
  }, [userExperiments, experimentKey]);

  const isControl = useMemo(() => {
    if (localStorageOverride !== null) return !localStorageOverride;
    return variant === EXPERIMENT_VARIANTS.CONTROL;
  }, [variant, localStorageOverride]);

  const isTreatment = useMemo(() => {
    if (localStorageOverride !== null) return localStorageOverride;
    return variant === EXPERIMENT_VARIANTS.TREATMENT;
  }, [variant, localStorageOverride]);

  const abTestLogValue = useMemo(() => {
    const experiments = userExperiments?.Data?.data;
    if (!experiments) return undefined;
    const obj = experiments.reduce<Record<string, string | undefined>>(
      (acc, { experimentKey, variant }) => ({ ...acc, [experimentKey]: variant }),
      {},
    );
    return JSON.stringify(obj);
  }, [userExperiments]);

  return {
    variant,
    isControl,
    isTreatment,
    isLoading,
    abTestLogValue,
  };
}
